import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../store/hooks';
import strings from '../../utils/strings';
import { CText, CLoader, SEO } from '../../components/common';
import {
  FiChevronLeft,
  FiChevronRight,
  FiChevronUp,
  FiChevronDown,
  FiFilter,
  FiSearch,
  FiTrendingUp,
  FiSend,
  FiX,
  FiArrowRight,
} from 'react-icons/fi';
import {
  searchCompanies,
  getAllCompanyNames,
  getFilterOptions,
  getSectorOverview,
  CompanyDetails,
  CompanySearchResult,
  FilterOptions,
} from '../../api/copilot';
import {
  ScatterChart, Scatter, XAxis, YAxis, ZAxis,
  Tooltip, ReferenceLine, ResponsiveContainer,
} from 'recharts';

// ─── Types ──────────────────────────────────────────────────────────

interface SectorData {
  name: string;
  companyCount: number;
  totalMcap: number;
  avgPE: number;
  avgROE: number;
  avgROCE: number;
  avgMcapSales: number;
  avgPB: number;
  avgEvEbitda: number;
  totalRevenue: number;
  totalProfit: number;
  avgOPM: number;
}

type SortKey = 'name' | 'companyCount' | 'totalMcap' | 'avgPE' | 'avgROE' | 'avgROCE' | 'avgMcapSales' | 'avgPB' | 'avgEvEbitda' | 'totalRevenue' | 'totalProfit' | 'avgOPM';

type TabId = 'sectors' | 'insights' | 'companies';

// ─── Helpers ────────────────────────────────────────────────────────

const formatMcap = (val: number) => {
  if (val >= 10000000) return `₹${(val / 10000000).toFixed(0)} Cr`;
  if (val >= 100000) return `₹${(val / 100000).toFixed(0)} L`;
  if (val > 0) return `₹${val.toLocaleString()}`;
  return '-';
};

const formatValue = (value: any): string => {
  if (value === null || value === undefined || value === '-') return '-';
  if (typeof value === 'number') {
    if (value >= 10000000) return `${(value / 10000000).toFixed(2)} Cr`;
    if (value >= 100000) return `${(value / 100000).toFixed(2)} L`;
    return value.toLocaleString();
  }
  return String(value);
};

const getValuationColor = (s: SectorData, borderColor: string) => {
  let score = 0;
  let count = 0;
  if (s.avgPE > 0) { score += s.avgPE < 20 ? 2 : s.avgPE < 35 ? 1 : 0; count++; }
  if (s.avgPB > 0) { score += s.avgPB < 2 ? 2 : s.avgPB < 4 ? 1 : 0; count++; }
  if (s.avgROE > 0) { score += s.avgROE > 15 ? 2 : s.avgROE > 8 ? 1 : 0; count++; }
  if (count === 0) return borderColor;
  const avg = score / count;
  if (avg >= 1.5) return '#22c55e';
  if (avg >= 0.8) return '#f59e0b';
  return '#ef4444';
};

const getCompanyMetricColor = (metric: string, value: number | null, defaultColor: string): string => {
  if (value === null || value === undefined) return defaultColor;
  switch (metric) {
    case 'pe': return value > 40 ? '#f59e0b' : defaultColor;
    case 'roe': return value > 15 ? '#22c55e' : value < 5 ? '#ef4444' : defaultColor;
    case 'roce': return value > 15 ? '#22c55e' : value < 5 ? '#ef4444' : defaultColor;
    case 'mcapSales': return value < 2 ? '#22c55e' : value > 5 ? '#f59e0b' : defaultColor;
    case 'pb': return value < 2 ? '#22c55e' : value > 4 ? '#f59e0b' : defaultColor;
    case 'evEbitda': return value < 15 ? '#22c55e' : value > 25 ? '#f59e0b' : defaultColor;
    case 'divYield': return value > 2 ? '#22c55e' : defaultColor;
    default: return defaultColor;
  }
};

// ─── Sub-components ─────────────────────────────────────────────────

const SortIcon = ({ sortKey: currentKey, col, sortAsc }: { sortKey: SortKey; col: SortKey; sortAsc: boolean }) => {
  if (currentKey !== col) return null;
  const Icon = sortAsc ? FiChevronUp : FiChevronDown;
  return <span className="inline-block ml-1 align-middle">{React.createElement(Icon as any, { size: 12, color: '#fff' })}</span>;
};

const ShimmerCell = ({ width = '60%', className = '' }: { width?: string; className?: string }) => (
  <td className={`px-3 py-3 ${className}`}>
    <div className="animate-pulse rounded" style={{ height: 14, width, backgroundColor: 'currentColor', opacity: 0.08 }} />
  </td>
);

const SectorTableSkeleton = ({ rows = 8, colCount, borderColor }: { rows?: number; colCount: number; borderColor: string }) => (
  <>
    {Array.from({ length: rows }).map((_, i) => (
      <tr key={i} style={{ borderBottom: `1px solid ${borderColor}` }}>
        <ShimmerCell width="70%" className="px-4" />
        {Array.from({ length: colCount - 1 }).map((_, j) => (
          <ShimmerCell key={j} width="50%" />
        ))}
      </tr>
    ))}
  </>
);

const InsightCardSkeleton = ({ colors }: { colors: any }) => (
  <div
    className="p-4 rounded-xl animate-pulse"
    style={{ backgroundColor: colors.inputBackground, border: `1px solid ${colors.borderColor}`, borderLeftColor: colors.borderColor, borderLeftWidth: 4 }}
  >
    <div className="rounded" style={{ height: 10, width: '40%', backgroundColor: colors.textColor, opacity: 0.06, marginBottom: 8 }} />
    <div className="rounded" style={{ height: 16, width: '65%', backgroundColor: colors.textColor, opacity: 0.08, marginBottom: 6 }} />
    <div className="rounded" style={{ height: 12, width: '45%', backgroundColor: colors.textColor, opacity: 0.06 }} />
  </div>
);

const higherIsBetter = new Set(['avgROE', 'avgROCE', 'avgOPM', 'totalRevenue', 'totalProfit']);

// ─── Main Component ─────────────────────────────────────────────────

const Market: React.FC = () => {
  const navigate = useNavigate();
  const colors = useAppSelector((state) => state.theme.theme);

  // ── Section refs ──
  const sectorsRef = useRef<HTMLDivElement>(null);
  const insightsRef = useRef<HTMLDivElement>(null);
  const companiesRef = useRef<HTMLDivElement>(null);
  const tabBarRef = useRef<HTMLDivElement>(null);
  const searchOverlayRef = useRef<HTMLDivElement>(null);
  const copilotRef = useRef<HTMLDivElement>(null);

  // ── Tab tracking ──
  const [activeTab, setActiveTab] = useState<TabId>('sectors');

  // ── Sector overview ──
  const [sectors, setSectors] = useState<SectorData[]>([]);
  const [sectorsLoading, setSectorsLoading] = useState(true);
  const [sectorSortKey, setSectorSortKey] = useState<SortKey>('totalMcap');
  const [sectorSortAsc, setSectorSortAsc] = useState(false);

  // ── Company filters ──
  const [selectedSector, setSelectedSector] = useState('');
  const [selectedIndustry, setSelectedIndustry] = useState('');
  const [filterCriteria, setFilterCriteria] = useState('');
  const [filterOptions, setFilterOptions] = useState<FilterOptions>({ sectors: [], industries: [], sector_industry_map: {} });

  // ── Company results ──
  const [searchResults, setSearchResults] = useState<CompanyDetails[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const pageSize = 20;

  // ── Company autocomplete ──
  const [companySearch, setCompanySearch] = useState('');
  const [allCompanies, setAllCompanies] = useState<string[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // ── Search popover + Copilot widget ──
  const [searchOpen, setSearchOpen] = useState(false);
  const [copilotOpen, setCopilotOpen] = useState(false);
  const [copilotQuery, setCopilotQuery] = useState('');

  // ── Build combined criteria ──
  const buildCombinedCriteria = useCallback((): string => {
    const parts: string[] = [];
    if (selectedSector) parts.push(`sector IN ('${selectedSector}')`);
    if (selectedIndustry) parts.push(`industry IN ('${selectedIndustry}')`);
    if (filterCriteria.trim()) parts.push(filterCriteria.trim());
    return parts.join(' AND ');
  }, [selectedSector, selectedIndustry, filterCriteria]);

  // ── Load companies ──
  const loadAllCompanies = useCallback(async (page: number = 1, criteria: string = '') => {
    setSearchLoading(true);
    setSearchError(null);
    try {
      const result: CompanySearchResult = await searchCompanies(criteria, page, pageSize);
      if (result.status === 'success' && result.data) {
        setSearchResults(result.data);
        setHasNextPage(result.pagination?.has_next || false);
        setCurrentPage(page);
      } else {
        setSearchError(result.error || 'Failed to load companies');
      }
    } catch (err: any) {
      console.error('Error searching companies:', err);
      setSearchError(err.message || 'Failed to search companies');
    } finally {
      setSearchLoading(false);
    }
  }, []);

  // ── Initial data loads (parallel) ──
  useEffect(() => {
    // Load first page of companies
    loadAllCompanies(1, '');

    // Load filter options
    const fetchFilterOptions = async () => {
      const options = await getFilterOptions();
      setFilterOptions(options);
    };
    fetchFilterOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Lazy-load company names when search overlay opens ──
  const companyNamesLoaded = useRef(false);
  useEffect(() => {
    if (searchOpen && !companyNamesLoaded.current) {
      companyNamesLoaded.current = true;
      setCompaniesLoading(true);
      getAllCompanyNames().then((names) => {
        setAllCompanies(names);
        setCompaniesLoading(false);
      });
    }
  }, [searchOpen]);

  // ── Load sector overview (single server-side aggregation call) ──
  useEffect(() => {
    const fetchSectors = async () => {
      setSectorsLoading(true);
      try {
        const data = await getSectorOverview();
        setSectors(data);
      } catch (err) {
        console.error('Failed to load sectors:', err);
      } finally {
        setSectorsLoading(false);
      }
    };
    fetchSectors();
  }, []);

  // ── Auto-search on dropdown filter change (skip initial mount — handled above) ──
  const filtersInitialized = useRef(false);
  useEffect(() => {
    if (!filtersInitialized.current) {
      filtersInitialized.current = true;
      return;
    }
    const criteria = buildCombinedCriteria();
    loadAllCompanies(1, criteria);
  }, [selectedSector, selectedIndustry, buildCombinedCriteria, loadAllCompanies]);

  // ── Close overlays on outside click ──
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
      if (searchOverlayRef.current && !searchOverlayRef.current.contains(event.target as Node)) {
        setSearchOpen(false);
      }
      if (copilotRef.current && !copilotRef.current.contains(event.target as Node)) {
        setCopilotOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ── Close overlays on Escape ──
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setSearchOpen(false);
        setCopilotOpen(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  // ── IntersectionObserver for active tab tracking ──
  useEffect(() => {
    const refs = [
      { id: 'sectors' as TabId, ref: sectorsRef },
      { id: 'insights' as TabId, ref: insightsRef },
      { id: 'companies' as TabId, ref: companiesRef },
    ];

    const observer = new IntersectionObserver(
      (entries) => {
        // Find the most visible section
        let maxRatio = 0;
        let visibleTab: TabId | null = null;
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio > maxRatio) {
            maxRatio = entry.intersectionRatio;
            const found = refs.find(r => r.ref.current === entry.target);
            if (found) visibleTab = found.id;
          }
        });
        if (visibleTab) setActiveTab(visibleTab);
      },
      { threshold: [0.1, 0.3, 0.5], rootMargin: '-80px 0px -40% 0px' }
    );

    refs.forEach(({ ref }) => {
      if (ref.current) observer.observe(ref.current);
    });

    return () => observer.disconnect();
  }, []);

  // ── Derived state ──
  const availableIndustries = selectedSector
    ? filterOptions.sector_industry_map[selectedSector] || []
    : filterOptions.industries;

  const totalCompanies = useMemo(() => sectors.reduce((sum, s) => sum + s.companyCount, 0), [sectors]);

  const sortedSectors = useMemo(() => {
    return [...sectors].sort((a, b) => {
      const aVal = a[sectorSortKey];
      const bVal = b[sectorSortKey];
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sectorSortAsc ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      return sectorSortAsc ? (aVal as number) - (bVal as number) : (bVal as number) - (aVal as number);
    });
  }, [sectors, sectorSortKey, sectorSortAsc]);

  // Insights
  const cheapestSector = useMemo(() => {
    const valid = sectors.filter(s => s.avgPE > 0);
    return valid.length > 0 ? valid.reduce((a, b) => a.avgPE < b.avgPE ? a : b) : null;
  }, [sectors]);

  const highestQuality = useMemo(() => {
    const valid = sectors.filter(s => s.avgROE > 0);
    return valid.length > 0 ? valid.reduce((a, b) => a.avgROE > b.avgROE ? a : b) : null;
  }, [sectors]);

  const mostValued = useMemo(() => {
    return sectors.length > 0 ? sectors.reduce((a, b) => a.totalMcap > b.totalMcap ? a : b) : null;
  }, [sectors]);

  const bestValueBuy = useMemo(() => {
    const valid = sectors.filter(s => s.avgPB > 0);
    return valid.length > 0 ? valid.reduce((a, b) => a.avgPB < b.avgPB ? a : b) : null;
  }, [sectors]);

  const hasInsights = !sectorsLoading && sectors.length > 0;

  const filteredCompanies = companySearch.trim()
    ? allCompanies.filter(c => c.toLowerCase().includes(companySearch.toLowerCase())).slice(0, 10)
    : [];

  // ── Handlers ──
  const handleSort = (key: SortKey) => {
    if (sectorSortKey === key) {
      setSectorSortAsc(!sectorSortAsc);
    } else {
      setSectorSortKey(key);
      setSectorSortAsc(key === 'name');
    }
  };

  const handleSearch = () => {
    setCurrentPage(1);
    loadAllCompanies(1, buildCombinedCriteria());
  };

  const handlePrevPage = () => {
    if (currentPage > 1) loadAllCompanies(currentPage - 1, buildCombinedCriteria());
  };

  const handleNextPage = () => {
    if (hasNextPage) loadAllCompanies(currentPage + 1, buildCombinedCriteria());
  };

  const handleCompanySelect = (companyName: string) => {
    setShowDropdown(false);
    setCompanySearch('');
    navigate(`/company/${encodeURIComponent(companyName)}`);
  };

  const handleSectorRowClick = (sectorName: string) => {
    navigate(`/sectors/${encodeURIComponent(sectorName)}`);
  };

  const scrollToSection = (tab: TabId) => {
    const refMap: Record<TabId, React.RefObject<HTMLDivElement | null>> = {
      sectors: sectorsRef,
      insights: insightsRef,
      companies: companiesRef,
    };
    refMap[tab].current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const copilotNudges = [
    'Screen high-ROCE stocks with low debt',
    'Analyze the Banking sector outlook',
    'Compare TCS vs Infosys fundamentals',
    'Find undervalued small-caps with strong growth',
  ];

  const handleCopilotSend = (query: string) => {
    if (!query.trim()) return;
    setCopilotOpen(false);
    setCopilotQuery('');
    navigate(`/copilot?q=${encodeURIComponent(query.trim())}`);
  };

  const clearSectorFilter = () => {
    setSelectedSector('');
    setSelectedIndustry('');
  };

  // ── Tab config ──
  const tabs: { id: TabId; label: string }[] = [
    { id: 'sectors', label: 'Sectors' },
    { id: 'insights', label: 'Insights' },
    { id: 'companies', label: 'Companies' },
  ];

  // ── Sector table columns ──
  const sectorColumns: [string, SortKey, boolean][] = [
    ['Sector', 'name', true],
    ['Companies', 'companyCount', false],
    ['Total MCap', 'totalMcap', false],
    ['Avg P/E', 'avgPE', false],
    ['Avg ROE', 'avgROE', false],
    ['Avg ROCE', 'avgROCE', false],
    ['MCap/Sales', 'avgMcapSales', false],
    ['Avg P/B', 'avgPB', false],
    ['EV/EBITDA', 'avgEvEbitda', false],
    ['Revenue', 'totalRevenue', false],
    ['Net Profit', 'totalProfit', false],
    ['OPM %', 'avgOPM', false],
  ];

  // ── RRG (Relative Rotation Graph) state ──
  const [rrgX, setRrgX] = useState<SortKey>('avgPE');
  const [rrgY, setRrgY] = useState<SortKey>('avgROE');
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);

  const rrgMetricKeys: SortKey[] = ['avgPE', 'avgROE', 'avgROCE', 'avgMcapSales', 'avgPB', 'avgEvEbitda', 'totalRevenue', 'totalProfit', 'avgOPM'];
  const metricLabels: Record<string, string> = {
    avgPE: 'Avg P/E', avgROE: 'Avg ROE', avgROCE: 'Avg ROCE',
    avgMcapSales: 'MCap/Sales', avgPB: 'Avg P/B', avgEvEbitda: 'EV/EBITDA',
    totalRevenue: 'Revenue', totalProfit: 'Net Profit', avgOPM: 'OPM %',
  };
  const rrgData = useMemo(() => {
    const allowNeg = new Set(['avgOPM', 'totalProfit']);
    const valid = sectors.filter(s => {
      const xVal = s[rrgX] as number;
      const yVal = s[rrgY] as number;
      const xOk = allowNeg.has(rrgX) ? xVal !== 0 : xVal > 0;
      const yOk = allowNeg.has(rrgY) ? yVal !== 0 : yVal > 0;
      return xOk && yOk;
    });
    if (valid.length < 2) return { points: [] };

    const meanX = valid.reduce((sum, s) => sum + (s[rrgX] as number), 0) / valid.length;
    const meanY = valid.reduce((sum, s) => sum + (s[rrgY] as number), 0) / valid.length;
    const madX = valid.reduce((sum, s) => sum + Math.abs((s[rrgX] as number) - meanX), 0) / valid.length || 1;
    const madY = valid.reduce((sum, s) => sum + Math.abs((s[rrgY] as number) - meanY), 0) / valid.length || 1;

    const points = valid.map(s => {
      const rawX = s[rrgX] as number;
      const rawY = s[rrgY] as number;
      const x = (rawX - meanX) / madX;
      const y = (rawY - meanY) / madY;
      const xGood = higherIsBetter.has(rrgX) ? x >= 0 : x <= 0;
      const yGood = higherIsBetter.has(rrgY) ? y >= 0 : y <= 0;
      let quadrant: string;
      let color: string;
      if (xGood && yGood) { quadrant = 'Outperforming'; color = '#22c55e'; }
      else if (!xGood && yGood) { quadrant = 'Weakening'; color = '#f59e0b'; }
      else if (xGood && !yGood) { quadrant = 'Recovering'; color = '#3b82f6'; }
      else { quadrant = 'Lagging'; color = '#ef4444'; }
      return { name: s.name, x, y, rawX, rawY, z: s.totalMcap, quadrant, color, idx: 0 };
    });
    points.forEach((p, i) => { p.idx = i + 1; });

    return { points };
  }, [sectors, rrgX, rrgY]);

  const quadrantCorners = useMemo(() => {
    const xr = higherIsBetter.has(rrgX);
    const yt = higherIsBetter.has(rrgY);
    return [
      { label: 'Outperforming', color: '#22c55e', top: yt, right: xr },
      { label: 'Weakening', color: '#f59e0b', top: yt, right: !xr },
      { label: 'Recovering', color: '#3b82f6', top: !yt, right: xr },
      { label: 'Lagging', color: '#ef4444', top: !yt, right: !xr },
    ];
  }, [rrgX, rrgY]);

  return (
    <div
      className="p-4 lg:p-8 min-h-screen pb-24"
      style={{ backgroundColor: colors.bgColor || colors.backgroundColor }}
    >
      <SEO
        title="Market - Sectors & Companies | Yantrai"
        description={`Explore ${sectors.length}+ Indian stock market sectors and 1000+ companies with valuation metrics. Compare sectors, filter companies, and deep-dive with AI.`}
        canonical="/market"
      />

      <div className="lg:max-w-6xl lg:mx-auto animate-fade-in">
        {/* ── Header ── */}
        <div className="mb-2">
          <h1 className="text-2xl lg:text-3xl font-bold gradient-text">{strings.market}</h1>
          <p className="mt-1" style={{ color: colors.grayScale500, fontSize: 14 }}>
            Explore {sectors.length > 0 ? sectors.length : '...'} sectors and {totalCompanies > 0 ? `${totalCompanies}+` : '1000+'} companies
          </p>
        </div>

        {/* ── Sticky Tab Bar ── */}
        <div
          ref={tabBarRef}
          className="sticky top-0 z-30 py-3 -mx-4 px-4 lg:-mx-8 lg:px-8 relative"
          style={{
            backgroundColor: colors.bgColor || colors.backgroundColor,
            borderBottom: `1px solid ${colors.borderColor}`,
            backdropFilter: 'blur(12px)',
          }}
        >
          <div className="flex items-center gap-2">
            <div className="flex gap-2 overflow-x-auto">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => scrollToSection(tab.id)}
                  className="px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200"
                  style={{
                    backgroundColor: activeTab === tab.id ? colors.primary : colors.inputBackground,
                    color: activeTab === tab.id ? '#fff' : colors.textColor,
                    border: `1px solid ${activeTab === tab.id ? colors.primary : colors.borderColor}`,
                  }}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            <button
              className="ml-auto p-2.5 rounded-full transition-all duration-200 hover:scale-110 active:scale-95 flex-shrink-0"
              style={{
                backgroundColor: searchOpen ? colors.primary : colors.inputBackground,
                border: `1px solid ${searchOpen ? colors.primary : colors.borderColor}`,
              }}
              onClick={() => setSearchOpen(!searchOpen)}
              title="Search company"
            >
              {React.createElement(FiSearch as any, { size: 16, color: searchOpen ? '#fff' : colors.textColor })}
            </button>
          </div>

          {/* Search Overlay */}
          {searchOpen && (
            <div
              ref={searchOverlayRef}
              className="absolute left-0 right-0 top-full p-4 lg:px-8 z-40"
              style={{
                backgroundColor: colors.bgColor || colors.backgroundColor,
                borderBottom: `1px solid ${colors.borderColor}`,
                boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
              }}
            >
              <div className="relative lg:max-w-6xl lg:mx-auto" ref={dropdownRef}>
                <input
                  type="text"
                  className="w-full p-3 pl-10 rounded-xl text-sm"
                  style={{
                    backgroundColor: colors.inputBackground,
                    border: `1px solid ${colors.borderColor}`,
                    color: colors.textColor,
                  }}
                  placeholder="Search company by name..."
                  value={companySearch}
                  onChange={(e) => {
                    setCompanySearch(e.target.value);
                    setShowDropdown(e.target.value.trim().length > 0);
                  }}
                  onFocus={() => companySearch.trim() && setShowDropdown(true)}
                  autoFocus
                />
                <div className="absolute left-3 top-1/2 -translate-y-1/2">
                  {React.createElement(FiSearch as any, { size: 16, color: colors.grayScale400 })}
                </div>
                {companiesLoading && (
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs" style={{ color: colors.grayScale400 }}>Loading...</span>
                )}

                {/* Autocomplete Dropdown */}
                {showDropdown && filteredCompanies.length > 0 && (
                  <div
                    className="absolute left-0 right-0 mt-1 max-h-64 overflow-y-auto rounded-xl shadow-lg z-50"
                    style={{
                      backgroundColor: colors.backgroundColor,
                      border: `1px solid ${colors.borderColor}`,
                    }}
                  >
                    {filteredCompanies.map((company, idx) => (
                      <div
                        key={company}
                        className="px-4 py-3 cursor-pointer transition-colors"
                        style={{
                          backgroundColor: idx % 2 === 0 ? colors.backgroundColor : colors.inputBackground,
                          borderBottom: idx < filteredCompanies.length - 1 ? `1px solid ${colors.borderColor}` : 'none',
                        }}
                        onClick={() => {
                          handleCompanySelect(company);
                          setSearchOpen(false);
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = colors.primary + '20')}
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.backgroundColor =
                            idx % 2 === 0 ? colors.backgroundColor : colors.inputBackground)
                        }
                      >
                        <CText type="M14" color={colors.textColor}>{company}</CText>
                      </div>
                    ))}
                  </div>
                )}
                {showDropdown && companySearch.trim() && filteredCompanies.length === 0 && !companiesLoading && (
                  <div
                    className="absolute left-0 right-0 mt-1 p-3 rounded-xl"
                    style={{
                      backgroundColor: colors.backgroundColor,
                      border: `1px solid ${colors.borderColor}`,
                    }}
                  >
                    <CText type="r12" color={colors.grayScale500}>No companies found</CText>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* ═══════════════ SECTION 1: Sector Comparison ═══════════════ */}
        <div ref={sectorsRef} className="mt-6 mb-8 scroll-mt-20">

          {/* ── RRG (Relative Rotation Graph) ── */}
          {sectors.length > 0 && (
            <div className="rounded-xl overflow-hidden mb-4" style={{ border: `1px solid ${colors.borderColor}` }}>
              <div className="p-4 flex items-center justify-between flex-wrap gap-3" style={{ backgroundColor: colors.inputBackground }}>
                <h3 className="text-base font-semibold" style={{ color: colors.textColor }}>Sector Rotation Graph</h3>
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-1.5 text-sm" style={{ color: colors.grayScale500 }}>
                    X:
                    <select
                      className="p-1.5 rounded-lg text-sm"
                      style={{ backgroundColor: colors.backgroundColor, border: `1px solid ${colors.borderColor}`, color: colors.textColor }}
                      value={rrgX}
                      onChange={(e) => setRrgX(e.target.value as SortKey)}
                    >
                      {rrgMetricKeys.map(k => <option key={k} value={k}>{metricLabels[k]}</option>)}
                    </select>
                  </label>
                  <label className="flex items-center gap-1.5 text-sm" style={{ color: colors.grayScale500 }}>
                    Y:
                    <select
                      className="p-1.5 rounded-lg text-sm"
                      style={{ backgroundColor: colors.backgroundColor, border: `1px solid ${colors.borderColor}`, color: colors.textColor }}
                      value={rrgY}
                      onChange={(e) => setRrgY(e.target.value as SortKey)}
                    >
                      {rrgMetricKeys.map(k => <option key={k} value={k}>{metricLabels[k]}</option>)}
                    </select>
                  </label>
                </div>
              </div>
              {rrgData.points.length > 0 ? (
              <div className="p-4" style={{ backgroundColor: colors.backgroundColor }}>
                <div style={{ display: 'flex', gap: 16 }}>
                {/* Chart */}
                <div className="relative" style={{ flex: 1, minWidth: 0, height: 450 }}>
                  {/* Quadrant corner labels */}
                  {quadrantCorners.map(q => (
                    <span
                      key={q.label}
                      className="absolute text-xs font-medium pointer-events-none"
                      style={{
                        color: q.color,
                        opacity: 0.35,
                        ...(q.top ? { top: 8 } : { bottom: 28 }),
                        ...(q.right ? { right: 8 } : { left: 48 }),
                        zIndex: 1,
                      }}
                    >
                      {q.label}
                    </span>
                  ))}
                  <ResponsiveContainer width="100%" height={450}>
                    <ScatterChart margin={{ top: 40, right: 50, bottom: 40, left: 30 }}>
                      <XAxis
                        type="number"
                        dataKey="x"
                        name={metricLabels[rrgX]}
                        tick={{ fontSize: 11, fill: colors.grayScale400 }}
                        label={{ value: `${metricLabels[rrgX]} (σ from mean)`, position: 'insideBottom', offset: -10, fontSize: 12, fill: colors.grayScale500 }}
                      />
                      <YAxis
                        type="number"
                        dataKey="y"
                        name={metricLabels[rrgY]}
                        tick={{ fontSize: 11, fill: colors.grayScale400 }}
                        label={{ value: `${metricLabels[rrgY]} (σ from mean)`, angle: -90, position: 'insideLeft', offset: 10, fontSize: 12, fill: colors.grayScale500 }}
                      />
                      <ZAxis type="number" dataKey="z" range={[200, 200]} />
                      <ReferenceLine x={0} stroke={colors.grayScale400} strokeDasharray="4 4" />
                      <ReferenceLine y={0} stroke={colors.grayScale400} strokeDasharray="4 4" />
                      <Tooltip
                        content={({ active, payload }: any) => {
                          if (!active || !payload?.length) return null;
                          const d = payload[0].payload;
                          return (
                            <div
                              className="px-3 py-2 rounded-lg shadow-lg text-xs"
                              style={{
                                backgroundColor: colors.cardBackground || colors.inputBackground,
                                border: `1px solid ${colors.borderColor}`,
                              }}
                            >
                              <div className="font-semibold mb-1" style={{ color: colors.textColor }}>{d.name}</div>
                              <div style={{ color: colors.grayScale500 }}>{metricLabels[rrgX]}: {d.rawX.toFixed(1)} ({d.x >= 0 ? '+' : ''}{d.x.toFixed(2)}σ)</div>
                              <div style={{ color: colors.grayScale500 }}>{metricLabels[rrgY]}: {d.rawY.toFixed(1)} ({d.y >= 0 ? '+' : ''}{d.y.toFixed(2)}σ)</div>
                              <div className="mt-1 font-medium" style={{ color: d.color }}>{d.quadrant}</div>
                            </div>
                          );
                        }}
                      />
                      <Scatter
                        data={rrgData.points}
                        shape={(props: any) => {
                          const { cx, cy, size, payload } = props;
                          const r = Math.sqrt((size || 400) / Math.PI);
                          return (
                            <g style={{ cursor: 'pointer' }}
                               onClick={() => navigate(`/sectors/${encodeURIComponent(payload.name)}`)}
                               onMouseEnter={() => setHoveredIdx(payload.idx)}
                               onMouseLeave={() => setHoveredIdx(null)}>
                              <circle cx={cx} cy={cy} r={r} fill={payload.color}
                                      fillOpacity={hoveredIdx === payload.idx ? 1 : 0.85}
                                      stroke={hoveredIdx === payload.idx ? '#fff' : 'none'}
                                      strokeWidth={2} />
                              <text x={cx} y={cy} textAnchor="middle" dominantBaseline="central"
                                    fill="#fff" fontSize={11} fontWeight={700}
                                    style={{ pointerEvents: 'none' }}>
                                {payload.idx}
                              </text>
                            </g>
                          );
                        }}
                      />
                    </ScatterChart>
                  </ResponsiveContainer>
                </div>
                {/* Legend grouped by quadrant */}
                <div style={{ width: 220, flexShrink: 0, overflowY: 'auto', maxHeight: 450, paddingTop: 20 }}>
                  {(['Outperforming', 'Weakening', 'Recovering', 'Lagging'] as const).map(quadrant => {
                    const group = rrgData.points.filter(p => p.quadrant === quadrant);
                    if (group.length === 0) return null;
                    return (
                      <div key={quadrant} style={{ marginBottom: 12 }}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: group[0].color, textTransform: 'uppercase', letterSpacing: 0.5, padding: '0 8px 4px' }}>
                          {quadrant}
                        </div>
                        {group.map(p => (
                          <div key={p.idx}
                               style={{
                                 display: 'flex', alignItems: 'center', gap: 8,
                                 padding: '3px 8px', borderRadius: 6, cursor: 'pointer',
                                 backgroundColor: hoveredIdx === p.idx ? (colors.cardBackground || colors.inputBackground) : 'transparent',
                                 transition: 'background-color 0.15s',
                               }}
                               onMouseEnter={() => setHoveredIdx(p.idx)}
                               onMouseLeave={() => setHoveredIdx(null)}
                               onClick={() => navigate(`/sectors/${encodeURIComponent(p.name)}`)}>
                            <span style={{
                              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                              width: 18, height: 18, borderRadius: '50%',
                              backgroundColor: p.color, color: '#fff',
                              fontSize: 9, fontWeight: 700, flexShrink: 0,
                            }}>{p.idx}</span>
                            <span style={{ fontSize: 11, color: hoveredIdx === p.idx ? colors.textColor : colors.grayScale500 }}>
                              {p.name}
                            </span>
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </div>
                </div>
              </div>
              ) : (
              <div className="p-8 text-center" style={{ backgroundColor: colors.backgroundColor }}>
                <CText type="r14" color={colors.grayScale400}>
                  Not enough data for {metricLabels[rrgX]} vs {metricLabels[rrgY]}. Try different metrics.
                </CText>
              </div>
              )}
            </div>
          )}

          <div className="rounded-xl overflow-hidden" style={{ border: `1px solid ${colors.borderColor}` }}>
            <div className="p-4 flex items-center justify-between" style={{ backgroundColor: colors.inputBackground }}>
              <div className="flex items-center gap-2">
                {React.createElement(FiTrendingUp as any, { size: 16, color: colors.primary })}
                <h2 className="text-base font-semibold" style={{ color: colors.textColor }}>Sector Comparison</h2>
              </div>
              {sectorsLoading && (
                <span className="text-xs" style={{ color: colors.grayScale400 }}>Loading...</span>
              )}
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ backgroundColor: colors.primary }}>
                    {sectorColumns.map(([label, key, isLeft]) => (
                      <th
                        key={key}
                        className={`px-3 py-2.5 text-white font-medium whitespace-nowrap cursor-pointer select-none hover:bg-white/10 transition-colors ${isLeft ? 'text-left px-4' : 'text-right'}`}
                        onClick={() => handleSort(key)}
                      >
                        {label}
                        <SortIcon sortKey={sectorSortKey} col={key} sortAsc={sectorSortAsc} />
                      </th>
                    ))}
                    {/* Deep-dive arrow column */}
                    <th className="px-2 py-2.5 w-10" />
                  </tr>
                </thead>
                <tbody>
                  {sortedSectors.length === 0 && sectorsLoading && (
                    <SectorTableSkeleton colCount={13} borderColor={colors.borderColor} />
                  )}
                  {sortedSectors.map((sector, idx) => {
                    return (
                      <tr
                        key={sector.name}
                        className="cursor-pointer transition-colors"
                        style={{
                          backgroundColor: idx % 2 === 0 ? colors.backgroundColor : colors.inputBackground,
                          borderBottom: `1px solid ${colors.borderColor}`,
                          borderLeft: '3px solid transparent',
                        }}
                        onClick={() => handleSectorRowClick(sector.name)}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = colors.primary + '08';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = idx % 2 === 0 ? colors.backgroundColor : colors.inputBackground;
                        }}
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: getValuationColor(sector, colors.borderColor) }} />
                            <CText type="M12" color={colors.textColor}>{sector.name}</CText>
                          </div>
                        </td>
                        <td className="px-3 py-3 text-right">
                          <CText type="r12" color={colors.textColor}>{sector.companyCount}</CText>
                        </td>
                        <td className="px-3 py-3 text-right">
                          <CText type="r12" color={colors.textColor}>{formatMcap(sector.totalMcap)}</CText>
                        </td>
                        <td className="px-3 py-3 text-right">
                          <CText type="r12" color={sector.avgPE > 30 ? '#f59e0b' : colors.textColor}>
                            {sector.avgPE > 0 ? `${sector.avgPE.toFixed(1)}x` : '-'}
                          </CText>
                        </td>
                        <td className="px-3 py-3 text-right">
                          <CText type="r12" color={sector.avgROE > 15 ? '#22c55e' : colors.textColor}>
                            {sector.avgROE > 0 ? `${sector.avgROE.toFixed(1)}%` : '-'}
                          </CText>
                        </td>
                        <td className="px-3 py-3 text-right">
                          <CText type="r12" color={sector.avgROCE > 15 ? '#22c55e' : colors.textColor}>
                            {sector.avgROCE > 0 ? `${sector.avgROCE.toFixed(1)}%` : '-'}
                          </CText>
                        </td>
                        <td className="px-3 py-3 text-right">
                          <CText type="r12" color={sector.avgMcapSales > 5 ? '#f59e0b' : sector.avgMcapSales > 0 && sector.avgMcapSales < 2 ? '#22c55e' : colors.textColor}>
                            {sector.avgMcapSales > 0 ? `${sector.avgMcapSales.toFixed(1)}x` : '-'}
                          </CText>
                        </td>
                        <td className="px-3 py-3 text-right">
                          <CText type="r12" color={sector.avgPB > 4 ? '#f59e0b' : sector.avgPB > 0 && sector.avgPB < 2 ? '#22c55e' : colors.textColor}>
                            {sector.avgPB > 0 ? `${sector.avgPB.toFixed(1)}x` : '-'}
                          </CText>
                        </td>
                        <td className="px-3 py-3 text-right">
                          <CText type="r12" color={sector.avgEvEbitda > 25 ? '#f59e0b' : sector.avgEvEbitda > 0 && sector.avgEvEbitda < 15 ? '#22c55e' : colors.textColor}>
                            {sector.avgEvEbitda > 0 ? `${sector.avgEvEbitda.toFixed(1)}x` : '-'}
                          </CText>
                        </td>
                        <td className="px-3 py-3 text-right">
                          <CText type="r12" color={colors.textColor}>
                            {sector.totalRevenue > 0 ? formatMcap(sector.totalRevenue) : '-'}
                          </CText>
                        </td>
                        <td className="px-3 py-3 text-right">
                          <CText type="r12" color={sector.totalProfit < 0 ? '#ef4444' : colors.textColor}>
                            {sector.totalProfit !== 0 ? formatMcap(sector.totalProfit) : '-'}
                          </CText>
                        </td>
                        <td className="px-3 py-3 text-right">
                          <CText type="r12" color={sector.avgOPM > 15 ? '#22c55e' : sector.avgOPM < 5 && sector.avgOPM !== 0 ? '#ef4444' : colors.textColor}>
                            {sector.avgOPM !== 0 ? `${sector.avgOPM.toFixed(1)}%` : '-'}
                          </CText>
                        </td>
                        <td className="px-2 py-3 text-center">
                          <button
                            className="p-1 rounded-full hover:bg-white/10 transition-colors"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/sectors/${encodeURIComponent(sector.name)}`);
                            }}
                            title={`Deep-dive into ${sector.name}`}
                          >
                            {React.createElement(FiArrowRight as any, { size: 14, color: colors.grayScale400 })}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* ═══════════════ SECTION 2: Sector Insights ═══════════════ */}
        <div ref={insightsRef} className="mb-8 scroll-mt-20">
          <h2 className="text-lg font-semibold mb-4" style={{ color: colors.textColor }}>Sector Insights</h2>
          {hasInsights ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {cheapestSector && (
                <button
                  onClick={() => navigate(`/sectors/${encodeURIComponent(cheapestSector.name)}`)}
                  className="p-4 rounded-xl text-left transition-all hover:-translate-y-0.5 hover:shadow-lg"
                  style={{ backgroundColor: colors.inputBackground, border: `1px solid ${colors.borderColor}`, borderLeftColor: '#22c55e', borderLeftWidth: 4 }}
                >
                  <CText type="r10" color={colors.grayScale400} style={{ display: 'block', marginBottom: 4 }}>Cheapest Sector</CText>
                  <CText type="M14" color={colors.textColor} style={{ display: 'block', fontWeight: 600 }}>{cheapestSector.name}</CText>
                  <CText type="r12" color="#22c55e">at {cheapestSector.avgPE.toFixed(1)}x P/E</CText>
                </button>
              )}
              {highestQuality && (
                <button
                  onClick={() => navigate(`/sectors/${encodeURIComponent(highestQuality.name)}`)}
                  className="p-4 rounded-xl text-left transition-all hover:-translate-y-0.5 hover:shadow-lg"
                  style={{ backgroundColor: colors.inputBackground, border: `1px solid ${colors.borderColor}`, borderLeftColor: '#22c55e', borderLeftWidth: 4 }}
                >
                  <CText type="r10" color={colors.grayScale400} style={{ display: 'block', marginBottom: 4 }}>Highest Quality</CText>
                  <CText type="M14" color={colors.textColor} style={{ display: 'block', fontWeight: 600 }}>{highestQuality.name}</CText>
                  <CText type="r12" color="#22c55e">with {highestQuality.avgROE.toFixed(1)}% ROE</CText>
                </button>
              )}
              {mostValued && (
                <button
                  onClick={() => navigate(`/sectors/${encodeURIComponent(mostValued.name)}`)}
                  className="p-4 rounded-xl text-left transition-all hover:-translate-y-0.5 hover:shadow-lg"
                  style={{ backgroundColor: colors.inputBackground, border: `1px solid ${colors.borderColor}`, borderLeftColor: colors.primary, borderLeftWidth: 4 }}
                >
                  <CText type="r10" color={colors.grayScale400} style={{ display: 'block', marginBottom: 4 }}>Most Valued</CText>
                  <CText type="M14" color={colors.textColor} style={{ display: 'block', fontWeight: 600 }}>{mostValued.name}</CText>
                  <CText type="r12" color={colors.primary}>at {formatMcap(mostValued.totalMcap)}</CText>
                </button>
              )}
              {bestValueBuy && (
                <button
                  onClick={() => navigate(`/sectors/${encodeURIComponent(bestValueBuy.name)}`)}
                  className="p-4 rounded-xl text-left transition-all hover:-translate-y-0.5 hover:shadow-lg"
                  style={{ backgroundColor: colors.inputBackground, border: `1px solid ${colors.borderColor}`, borderLeftColor: '#22c55e', borderLeftWidth: 4 }}
                >
                  <CText type="r10" color={colors.grayScale400} style={{ display: 'block', marginBottom: 4 }}>Best Value-Buy</CText>
                  <CText type="M14" color={colors.textColor} style={{ display: 'block', fontWeight: 600 }}>{bestValueBuy.name}</CText>
                  <CText type="r12" color="#22c55e">at {bestValueBuy.avgPB.toFixed(1)}x P/B</CText>
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {Array.from({ length: 4 }).map((_, i) => (
                <InsightCardSkeleton key={i} colors={colors} />
              ))}
            </div>
          )}
        </div>

        {/* ═══════════════ SECTION 3: Company Explorer ═══════════════ */}
        <div ref={companiesRef} className="mb-8 scroll-mt-20">
          <h2 className="text-lg font-semibold mb-4" style={{ color: colors.textColor }}>Company Explorer</h2>

          {/* Active filter chips */}
          {selectedSector && (
            <div className="flex flex-wrap gap-2 mb-3">
              <span
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium"
                style={{ backgroundColor: colors.primary + '18', color: colors.primary, border: `1px solid ${colors.primary}40` }}
              >
                {selectedSector}
                <button onClick={clearSectorFilter} className="hover:opacity-70">
                  {React.createElement(FiX as any, { size: 14 })}
                </button>
              </span>
              {selectedIndustry && (
                <span
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium"
                  style={{ backgroundColor: colors.primary + '18', color: colors.primary, border: `1px solid ${colors.primary}40` }}
                >
                  {selectedIndustry}
                  <button onClick={() => setSelectedIndustry('')} className="hover:opacity-70">
                    {React.createElement(FiX as any, { size: 14 })}
                  </button>
                </span>
              )}
            </div>
          )}

          {/* Sector & Industry Dropdowns */}
          <div
            className="p-5 rounded-2xl mb-4 card-shadow"
            style={{
              backgroundColor: colors.cardBackground || colors.inputBackground,
              border: `1px solid ${colors.borderColor}`,
            }}
          >
            <div className="flex items-center gap-2 mb-3">
              {React.createElement(FiFilter as any, { size: 18, color: colors.primary })}
              <CText type="M14" color={colors.textColor}>Filter by Sector & Industry</CText>
            </div>
            <div className="flex gap-3 flex-wrap">
              <select
                className="flex-1 min-w-[200px] p-3 rounded-xl text-sm"
                style={{
                  backgroundColor: colors.backgroundColor,
                  border: `1px solid ${colors.borderColor}`,
                  color: colors.textColor,
                }}
                value={selectedSector}
                onChange={(e) => {
                  setSelectedSector(e.target.value);
                  setSelectedIndustry('');
                }}
              >
                <option value="">All Sectors</option>
                {filterOptions.sectors.map((sector) => (
                  <option key={sector} value={sector}>{sector}</option>
                ))}
              </select>
              <select
                className="flex-1 min-w-[200px] p-3 rounded-xl text-sm"
                style={{
                  backgroundColor: colors.backgroundColor,
                  border: `1px solid ${colors.borderColor}`,
                  color: colors.textColor,
                }}
                value={selectedIndustry}
                onChange={(e) => setSelectedIndustry(e.target.value)}
              >
                <option value="">All Industries</option>
                {availableIndustries.map((industry) => (
                  <option key={industry} value={industry}>{industry}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Advanced Filter Input */}
          <div
            className="p-5 rounded-2xl mb-6 card-shadow"
            style={{
              backgroundColor: colors.cardBackground || colors.inputBackground,
              border: `1px solid ${colors.borderColor}`,
            }}
          >
            <div className="flex items-center gap-2 mb-3">
              {React.createElement(FiFilter as any, { size: 18, color: colors.primary })}
              <CText type="M14" color={colors.textColor}>Advanced Filter</CText>
            </div>
            <div className="flex gap-3">
              <input
                type="text"
                className="flex-1 p-3 rounded-xl text-sm focus-glow transition-all duration-200"
                style={{
                  backgroundColor: colors.backgroundColor,
                  border: `1px solid ${colors.borderColor}`,
                  color: colors.textColor,
                }}
                placeholder="e.g., market_cap > 1000000 AND ROE > 15"
                value={filterCriteria}
                onChange={(e) => setFilterCriteria(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
              <button
                className="px-6 py-3 rounded-xl font-semibold transition-all duration-200 hover:-translate-y-0.5 active:scale-95"
                style={{
                  backgroundColor: colors.primary,
                  color: colors.white,
                  boxShadow: colors.shadowMedium,
                }}
                onClick={handleSearch}
                disabled={searchLoading}
              >
                {searchLoading ? 'Loading...' : 'Search'}
              </button>
            </div>
            <CText type="r12" color={colors.grayScale400} style={{ marginTop: 10 }}>
              Examples: market_cap {'>'} 1000000 | price_to_earnings {'<'} 20 | ROE {'>'} 15 | ev_to_ebitda {'<'} 15
            </CText>
          </div>

          {/* Loading State */}
          {searchLoading && (
            <div className="flex justify-center items-center py-12">
              <CLoader />
            </div>
          )}

          {/* Error State */}
          {searchError && !searchLoading && (
            <div className="text-center py-8">
              <CText type="M14" color={colors.alertColor}>{searchError}</CText>
              <button
                className="mt-4 px-4 py-2 rounded-lg"
                style={{ backgroundColor: colors.primary, color: colors.white }}
                onClick={() => loadAllCompanies(currentPage, buildCombinedCriteria())}
              >
                Retry
              </button>
            </div>
          )}

          {/* Company Table */}
          {!searchLoading && !searchError && searchResults.length > 0 && (
            <>
              <div
                className="overflow-x-auto rounded-2xl card-shadow"
                style={{ border: `1px solid ${colors.borderColor}` }}
              >
                <table className="w-full text-sm">
                  <thead>
                    <tr style={{ background: `linear-gradient(135deg, ${colors.primary}, #9F03F2)` }}>
                      <th className="px-4 py-4 text-left text-white font-semibold">Company</th>
                      <th className="px-4 py-4 text-left text-white font-semibold">Sector</th>
                      <th className="px-4 py-4 text-left text-white font-semibold">Industry</th>
                      <th className="px-4 py-4 text-right text-white font-semibold">Market Cap</th>
                      <th className="px-4 py-4 text-right text-white font-semibold">Price</th>
                      <th className="px-4 py-4 text-right text-white font-semibold">P/E</th>
                      <th className="px-4 py-4 text-right text-white font-semibold">ROCE</th>
                      <th className="px-4 py-4 text-right text-white font-semibold">ROE</th>
                      <th className="px-4 py-4 text-right text-white font-semibold">MCap/Sales</th>
                      <th className="px-4 py-4 text-right text-white font-semibold">EV/EBITDA</th>
                      <th className="px-4 py-4 text-right text-white font-semibold">P/B Ratio</th>
                    </tr>
                  </thead>
                  <tbody>
                    {searchResults.map((company, idx) => {
                      const pe = company.price_to_earnings ? Number(company.price_to_earnings) : null;
                      const roe = company.roe ? Number(company.roe) : null;
                      const roce = company.roce ? Number(company.roce) : null;
                      const mcapSales = company.market_cap_to_sales ? Number(company.market_cap_to_sales) : null;
                      const pb = company.price_to_book ? Number(company.price_to_book) : null;
                      const evEbitda = company.ev_to_ebitda ? Number(company.ev_to_ebitda) : null;

                      return (
                        <tr
                          key={company.name || idx}
                          className="border-b transition-colors cursor-pointer"
                          style={{
                            backgroundColor: idx % 2 === 0 ? colors.backgroundColor : colors.inputBackground,
                            borderColor: colors.borderColor,
                          }}
                          onClick={() => navigate(`/company/${encodeURIComponent(company.name)}`)}
                          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = colors.primary + '10')}
                          onMouseLeave={(e) =>
                            (e.currentTarget.style.backgroundColor =
                              idx % 2 === 0 ? colors.backgroundColor : colors.inputBackground)
                          }
                        >
                          <td className="px-4 py-3">
                            <CText type="M14" color={colors.textColor}>{company.name}</CText>
                          </td>
                          <td className="px-4 py-3">
                            <CText type="r12" color={colors.grayScale500}>{company.sector || '-'}</CText>
                          </td>
                          <td className="px-4 py-3">
                            <CText type="r12" color={colors.grayScale500}>{company.industry || '-'}</CText>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <CText type="r12" color={colors.textColor}>{formatValue(company.market_cap)}</CText>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <CText type="r12" color={colors.textColor}>{formatValue(company.current_price)}</CText>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <CText type="r12" color={getCompanyMetricColor('pe', pe, colors.textColor)}>
                              {pe ? pe.toFixed(1) : '-'}
                            </CText>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <CText type="r12" color={getCompanyMetricColor('roce', roce, colors.textColor)}>
                              {roce ? roce.toFixed(1) : '-'}
                            </CText>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <CText type="r12" color={getCompanyMetricColor('roe', roe, colors.textColor)}>
                              {roe ? roe.toFixed(1) : '-'}
                            </CText>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <CText type="r12" color={getCompanyMetricColor('mcapSales', mcapSales, colors.textColor)}>
                              {mcapSales ? mcapSales.toFixed(2) : '-'}
                            </CText>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <CText type="r12" color={getCompanyMetricColor('evEbitda', evEbitda, colors.textColor)}>
                              {evEbitda ? evEbitda.toFixed(2) : '-'}
                            </CText>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <CText type="r12" color={getCompanyMetricColor('pb', pb, colors.textColor)}>
                              {pb ? pb.toFixed(2) : '-'}
                            </CText>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div
                className="flex items-center justify-center gap-4 mt-6 p-4 rounded-2xl card-shadow"
                style={{ backgroundColor: colors.cardBackground || colors.inputBackground }}
              >
                <button
                  className="flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold transition-all duration-200 hover:-translate-y-0.5 active:scale-95"
                  style={{
                    backgroundColor: currentPage > 1 ? colors.primary : colors.grayScale400,
                    color: colors.white,
                    opacity: currentPage > 1 ? 1 : 0.5,
                    boxShadow: currentPage > 1 ? colors.shadowMedium : 'none',
                  }}
                  onClick={handlePrevPage}
                  disabled={currentPage <= 1 || searchLoading}
                >
                  {React.createElement(FiChevronLeft as any, { size: 18 })}
                  Previous
                </button>
                <div className="px-4 py-2 rounded-xl" style={{ backgroundColor: colors.inputBackground }}>
                  <CText type="M14" color={colors.textColor}>Page {currentPage}</CText>
                </div>
                <button
                  className="flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold transition-all duration-200 hover:-translate-y-0.5 active:scale-95"
                  style={{
                    backgroundColor: hasNextPage ? colors.primary : colors.grayScale400,
                    color: colors.white,
                    opacity: hasNextPage ? 1 : 0.5,
                    boxShadow: hasNextPage ? colors.shadowMedium : 'none',
                  }}
                  onClick={handleNextPage}
                  disabled={!hasNextPage || searchLoading}
                >
                  Next
                  {React.createElement(FiChevronRight as any, { size: 18 })}
                </button>
              </div>
            </>
          )}

          {/* Empty State */}
          {!searchLoading && !searchError && searchResults.length === 0 && (
            <div className="text-center py-12">
              <CText type="M16" color={colors.grayScale500}>No companies found</CText>
              <CText type="r14" color={colors.grayScale400} style={{ marginTop: 8 }}>
                Try adjusting your filter criteria
              </CText>
            </div>
          )}
        </div>

      </div>

      {/* ═══════════════ Copilot Widget (Right-middle) ═══════════════ */}
      <div
        ref={copilotRef}
        className="fixed z-50"
        style={{ right: 0, top: '50%', transform: 'translateY(-50%)' }}
      >
        {!copilotOpen ? (
          <button
            onClick={() => setCopilotOpen(true)}
            className="flex items-center gap-2 px-4 py-3 rounded-l-full font-semibold text-sm transition-all duration-200 hover:scale-105 active:scale-95"
            style={{
              backgroundColor: colors.primary,
              color: '#fff',
              boxShadow: `0 4px 20px ${colors.primary}60`,
            }}
          >
            <span>✨</span>
            <span className="hidden sm:inline">Ask AI</span>
          </button>
        ) : (
          <div
            className="rounded-l-2xl overflow-hidden"
            style={{
              backgroundColor: colors.cardBackground || colors.inputBackground,
              border: `1px solid ${colors.borderColor}`,
              borderRight: 'none',
              boxShadow: '-4px 0 24px rgba(0,0,0,0.15)',
              width: 320,
            }}
          >
            <div className="p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span>✨</span>
                  <CText type="M14" color={colors.textColor}>Ask AI about stocks</CText>
                </div>
                <button
                  onClick={() => setCopilotOpen(false)}
                  className="p-1 rounded-full hover:opacity-70 transition-opacity"
                >
                  {React.createElement(FiX as any, { size: 16, color: colors.grayScale400 })}
                </button>
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  className="flex-1 p-2.5 rounded-lg text-sm"
                  style={{
                    backgroundColor: colors.backgroundColor,
                    border: `1px solid ${colors.borderColor}`,
                    color: colors.textColor,
                  }}
                  placeholder="Type a question..."
                  value={copilotQuery}
                  onChange={(e) => setCopilotQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleCopilotSend(copilotQuery)}
                  autoFocus
                />
                <button
                  className="p-2.5 rounded-lg transition-all duration-200 hover:scale-105 active:scale-95"
                  style={{
                    backgroundColor: colors.primary,
                    color: '#fff',
                  }}
                  onClick={() => handleCopilotSend(copilotQuery)}
                >
                  {React.createElement(FiSend as any, { size: 16 })}
                </button>
              </div>
              <div className="mt-3">
                <CText type="r12" color={colors.grayScale400} style={{ display: 'block', marginBottom: 8 }}>
                  💡 Try:
                </CText>
                <div className="flex flex-col gap-1.5">
                  {copilotNudges.map((nudge) => (
                    <button
                      key={nudge}
                      onClick={() => handleCopilotSend(nudge)}
                      className="text-left px-3 py-2 rounded-lg text-sm transition-colors"
                      style={{
                        backgroundColor: colors.backgroundColor,
                        color: colors.textColor,
                        border: `1px solid ${colors.borderColor}`,
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = colors.primary + '15')}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = colors.backgroundColor)}
                    >
                      {nudge}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Market;
